import React, { useState } from 'react'
import { Box, Button, Typography } from '@mui/material'
import ProzoDataGrid from '../../../common/ProzoDataGrid'
import { serviceLabel } from '../apiMap'

/* ─────────────────────────────────────────────────────────────────────────────
   reviewBits — the shared building blocks of the D2C review surfaces (the Deal
   Review page and the Proposal History modal): the command-header language, the
   grouped action deck, numbered section titles, and the tinted RFI matrices.
   One source so both screens can never drift apart visually.
   ──────────────────────────────────────────────────────────────────────────── */

// These reference tables are 3–20 static rows — no sorting/filtering/pagination,
// just the admin panel's standard grid look (same ProzoDataGrid the shipment tables use).
export const cols = (arr) => arr.map((c) => ({ sortable: false, filterable: false, ...c }))
export const miniGrid = (columns, rows, getRowId) => (
  <ProzoDataGrid
    flat={false} columns={columns} rows={rows} getRowId={getRowId}
    autoHeight hideFooter paginationMode="client" rowCount={rows.length}
  />
)

// Numbered section title — badge and text flex-centred on one line, so they stay
// aligned at any font size (a vertical-align offset on the badge alone drifts).
export const SecTitle = ({ n, children }) => (
  <Box component="span" sx={{ display: 'inline-flex', alignItems: 'center', gap: 1 }}>
    <Box component="span" sx={{ display: 'inline-grid', placeItems: 'center', width: 22, height: 22, borderRadius: '50%', bgcolor: '#EAF1FC', color: '#103A75', fontSize: 12, fontWeight: 800, flexShrink: 0 }}>{n}</Box>
    <Box component="span" sx={{ display: 'inline-flex', alignItems: 'center' }}>{children}</Box>
  </Box>
)

// ── Command-header building blocks (mockup "Direction 2", approved Aug 2026) ──
export const onBluePill = { bgcolor: 'rgba(255,255,255,0.18)', color: '#fff' }
export const statLbl = { fontSize: 10, fontWeight: 700, textTransform: 'uppercase', letterSpacing: '0.08em', opacity: 0.75 }
export const statVal = { fontSize: 17, fontWeight: 800, lineHeight: 1.3 }
export const statSub = { fontSize: 11, opacity: 0.8 }

// Real-button hierarchy for the white action deck.
export const btnPri = { textTransform: 'none', fontSize: 13.5, fontWeight: 700, borderRadius: '9px', px: 2.2, py: 1, bgcolor: '#1B9E55', color: '#fff', boxShadow: '0 2px 6px rgba(27,158,85,0.35)', '&:hover': { bgcolor: '#158044' }, '&.Mui-disabled': { bgcolor: '#E7EAF0', color: '#9AA4B2', boxShadow: 'none' } }
export const btnBlue = { textTransform: 'none', fontSize: 13.5, fontWeight: 700, borderRadius: '9px', px: 1.9, py: 0.9, bgcolor: '#fff', color: '#103A75', border: '1.5px solid #CFE0F7', '&:hover': { bgcolor: '#EAF1FC', border: '1.5px solid #2065D1' }, '&.Mui-disabled': { color: 'text.disabled', border: '1.5px solid #E7EAF0', bgcolor: '#F6F7FA' } }
export const btnGhost = { textTransform: 'none', fontSize: 13.5, fontWeight: 700, borderRadius: '9px', px: 1.9, py: 0.9, bgcolor: '#fff', color: 'text.secondary', border: '1.5px solid #C7D0E0', '&:hover': { color: '#2065D1', bgcolor: '#F5F9FE', border: '1.5px solid #2065D1' }, '&.Mui-disabled': { color: 'text.disabled', border: '1.5px solid #E7EAF0', bgcolor: '#F6F7FA' } }
export const btnDanger = { textTransform: 'none', fontSize: 13.5, fontWeight: 700, borderRadius: '9px', px: 1.9, py: 0.9, bgcolor: '#fff', color: '#B42318', border: '1.5px solid #F3C6C0', '&:hover': { bgcolor: '#FDECEA', border: '1.5px solid #B42318' } }

// Approved "Option G": the actions live on a white deck under the gradient band, in
// labelled clusters — DECIDE / REWORK / SEND BACK — so no button floats in a flat line.
const GROUP_TONE = {
  decide: { bg: '#F7FCF9', bd: '#D8E7DC', fg: '#15803D' },
  rework: { bg: '#F3F7FE', bd: '#CFE0F7', fg: '#103A75' },
  send: { bg: '#FAFBFC', bd: '#E3E9F1', fg: '#5C6B82' },
}
export const ActionGroup = ({ label, tone, children }) => {
  const t = GROUP_TONE[tone]
  return (
    <Box sx={{ border: `1px solid ${t.bd}`, bgcolor: t.bg, borderRadius: '12px', px: 1.5, pt: 1, pb: 1.25 }}>
      <Typography sx={{ fontSize: 9.5, fontWeight: 800, letterSpacing: '0.08em', textTransform: 'uppercase', color: t.fg, mb: 0.75 }}>{label}</Typography>
      <Box sx={{ display: 'flex', gap: 1, flexWrap: 'wrap', alignItems: 'center' }}>{children}</Box>
    </Box>
  )
}

// Role chips — same tinted-chip language as the forward-shipments table.
export const ROLE_CHIP = {
  SL: { bg: '#F4EEFA', fg: '#5B2D8E' },
  L1: { bg: '#EAF1FC', fg: '#103A75' },
  FH: { bg: '#FDF1E5', fg: '#E65100' },
}

const RFI_ZONE_ORDER = ['Local', 'Within State', 'Metros', 'ROI', 'Special']

// Order-level RFI pivoted per service into the same slab × zone matrix shape the SL
// entered it in (and the same shape as the margin heatmap), instead of the flat
// one-row-per-service×zone×slab list, which at 10 slabs × 5 zones × 2 services ran
// to 100 rows. % cells carry a blue tint scaled by volume share — heavier lanes
// literally look heavier.
export function rfiMatricesFromVersion(version) {
  const entries = version?.rfiEntries || []
  const byService = {}
  entries.forEach((e) => { (byService[e.serviceType] = byService[e.serviceType] || []).push(e) })
  return Object.entries(byService).map(([st, list]) => {
    const zones = [
      ...RFI_ZONE_ORDER.filter((z) => list.some((e) => e.zone === z)),
      ...[...new Set(list.map((e) => e.zone))].filter((z) => !RFI_ZONE_ORDER.includes(z)),
    ]
    const slabs = [...new Set(list.map((e) => e.weightSlab))].sort((a, b) => (parseFloat(a) || 0) - (parseFloat(b) || 0))
    const cell = {}
    list.forEach((e) => { (cell[e.weightSlab] = cell[e.weightSlab] || {})[e.zone] = e.splitPct })
    const maxPct = Math.max(...list.map((e) => Number(e.splitPct) || 0), 0)
    const rows = slabs.map((slab) => ({
      id: slab,
      slab,
      ...Object.fromEntries(zones.map((z) => [z, cell[slab]?.[z] ?? null])),
    }))
    const columns = cols([
      { field: 'slab', headerName: 'Weight slab', minWidth: 110, flex: 0.8, renderCell: ({ row }) => <b>{row.slab}</b> },
      ...zones.map((z) => ({
        field: z, headerName: z, minWidth: 100, flex: 1, align: 'right', headerAlign: 'right',
        renderCell: ({ row }) => {
          const v = row[z]
          if (v == null) return <Typography sx={{ fontSize: 12, color: 'text.disabled' }}>—</Typography>
          const t = maxPct > 0 ? v / maxPct : 0
          return <Box component="span" sx={{ px: 1, py: 0.4, borderRadius: '6px', fontSize: 12, fontWeight: 700, color: '#103A75', bgcolor: `rgba(32,101,209,${(0.05 + 0.28 * t).toFixed(3)})` }}>{v}%</Box>
        },
      })),
    ])
    return { serviceType: st, service: serviceLabel(st), rows, columns }
  })
}

// RfiMatrix — one service's volume-split grid, capped at 6 rows with a Show more /
// Show less toggle (same dashed-button language as the wizard's "Add next weight
// band") so a 10–12-slab structure doesn't swallow the page.
const MATRIX_LIMIT = 6
export function RfiMatrix({ matrix }) {
  const [open, setOpen] = useState(false)
  const isLong = matrix.rows.length > MATRIX_LIMIT + 1
  const rows = isLong && !open ? matrix.rows.slice(0, MATRIX_LIMIT) : matrix.rows
  return (
    <Box sx={{ mb: 1.5, '&:last-child': { mb: 0 } }}>
      <Typography sx={{ fontSize: 12, fontWeight: 700, color: 'text.secondary', mb: 0.5 }}>
        {matrix.service} volume split <Typography component="span" sx={{ fontSize: 11, fontWeight: 400, color: 'text.secondary' }}>(% of shipments, weight slab × zone)</Typography>
      </Typography>
      <ProzoDataGrid
        flat={false} columns={matrix.columns} rows={rows} autoHeight hideFooter
        paginationMode="client" rowCount={rows.length}
      />
      {isLong && (
        <Button fullWidth size="small" onClick={() => setOpen((v) => !v)} sx={{
          mt: 0.75, textTransform: 'none', fontSize: 12, fontWeight: 700, borderRadius: '8px', py: 0.6,
          color: 'primary.main', border: '1.5px dashed', borderColor: '#CFE0F7', bgcolor: '#F3F7FE',
          '&:hover': { bgcolor: '#EAF1FC', borderColor: 'primary.main' },
        }}>
          {open ? 'Show less ▴' : `Show all ${matrix.rows.length} slabs ▾`}
        </Button>
      )}
    </Box>
  )
}
